import { useState, useCallback, useRef, useEffect } from 'react';
import { loadPersistedWorkoutSession, savePersistedWorkoutSession } from '../services/workoutSessionPersistence';
import posthog from 'posthog-js';
import { generateWorkoutData } from '../services/geminiService';
import { processAIResponseToSegments } from '../services/workoutProcessor';
import { buildZoneContextPreamble } from '../services/promptZoneContext';
import { generateZwo, generateIntervalsMarkdown } from '@/features/export-hub/services/exporters';
import { supabase } from '@/core/supabaseClient';
import { buildWorkoutInsertRow } from '@/features/history/services/workoutPersistence';
import { TRANSLATIONS } from '@/core/i18n/translations';
import { GEMINI_CONFIG } from '@/core/apiConfig';
import { toast } from 'react-hot-toast';
import type {
  AIWorkoutResponse,
  WorkoutSegment,
  SportType,
  RunIntensity,
  IntensityTarget,
  ZoneDefinition,
} from '@/types';
import type { HistoryCachedPayload } from '@/types/historyPayload';

type CachedResult = HistoryCachedPayload;

interface ResultsCache {
  [key: string]: CachedResult;
}

/**
 * Enqueues async workout generation (Netlify job + background worker), polls every 3s via
 * `generate-workout-status`, caches results per sport/run-intensity, persists to Supabase when logged in,
 * and enforces guest limits / PRO gating.
 */
export function useWorkoutGenerator(
  session: any,
  user: any,
  currentPrompt: string,
  activeZones: ZoneDefinition[],
  sport: SportType,
  runIntensity: RunIntensity,
  language: 'en' | 'pl',
  fetchWorkouts: () => Promise<void>,
  guestGenerations: number,
  setGuestGenerations: React.Dispatch<React.SetStateAction<number>>,
  setShowGuestLimitModal: React.Dispatch<React.SetStateAction<boolean>>,
  isPro: boolean,
  setShowPricingModal: React.Dispatch<React.SetStateAction<boolean>>,
  setShowLandingPage: React.Dispatch<React.SetStateAction<boolean>>,
  onWorkoutSavedToHistory?: () => void,
  activeIntensity?: IntensityTarget,
  onRestorePersistedWorkout?: (meta: { cacheKey: string; sourcePrompt: string }) => void,
  isZonesValid: boolean = true,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [generationPhaseLabel, setGenerationPhaseLabel] = useState<string | null>(null);
  const [resultsCache, setResultsCache] = useState<ResultsCache>({});
  const [activePlatform, setActivePlatform] = useState<'tp' | 'intervals'>('tp');
  const generationCancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      generationCancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!onRestorePersistedWorkout) return;
    const stored = loadPersistedWorkoutSession();
    if (!stored) return;
    const row = stored.resultsCache[stored.lastKey];
    if (!row?.segments?.length) return;
    setResultsCache(stored.resultsCache);
    onRestorePersistedWorkout({
      cacheKey: stored.lastKey,
      sourcePrompt: row.sourcePrompt ?? '',
    });
  }, [onRestorePersistedWorkout]);

  const t = TRANSLATIONS[language];
  const currentCacheKey = sport === 'bike' ? 'bike' : `run-${runIntensity}`;
  const currentResult = resultsCache[currentCacheKey];

  const segments = currentResult?.segments || [];
  const aiWorkout = currentResult?.aiWorkout ?? null;
  const generatedXml = currentResult?.xml || '';
  const generatedMarkdown = currentResult?.markdown || '';
  const workoutName = currentResult?.workoutName || 'workout';
  const currentWorkoutId = currentResult?.workoutId;

  const handleGenerate = useCallback(async () => {
    // Hard-gate generation on a valid zone table — invalid bounds would break both the AI
    // step parser (averages min/max) and the Garmin export builder (serializes the same values).
    if (!isZonesValid) {
      toast.error(t.zonesInvalidGenerateBlocked);
      return;
    }
    if (!session) {
      if (guestGenerations >= GEMINI_CONFIG.GUEST_GENERATION_LIMIT) {
        setShowGuestLimitModal(true);
        toast.error(t.guestLimitExceeded);
        setIsLoading(false);
        return;
      }
    } else if (session && !isPro) {
      // Authenticated but not PRO, open pricing modal
      setShowPricingModal(true);
      toast.error(t.proFeatureRequired);
      return;
    }

    setIsLoading(true);
    generationCancelledRef.current = false;
    setGenerationPhaseLabel(t.generationStatusOrchestrating);

    try {
      const {
        data: { session: supabaseSession },
      } = await supabase.auth.getSession();
      const accessToken = supabaseSession?.access_token ?? null;

      // Build a deterministic preamble that pins the model to the user's CURRENT zones +
      // intensity target. This is the bridge between the editable UI and the LLM, so when the
      // user shrinks Z3 to 75-85% the model emits intensities consistent with that, not the
      // module-level defaults frozen at app boot.
      const effectiveTarget: IntensityTarget =
        activeIntensity ?? (sport === 'bike' ? 'power' : runIntensity);
      const zonePreamble = buildZoneContextPreamble(sport, effectiveTarget, activeZones, language);
      const enrichedPrompt = `${zonePreamble}${currentPrompt}`;

      const aiResponse = await generateWorkoutData(
        enrichedPrompt,
        accessToken,
        session ? null : guestGenerations + 1,
        language,
        (tick) => {
          if (generationCancelledRef.current) return;
          setGenerationPhaseLabel(
            tick % 2 === 0 ? t.generationStatusVerifying : t.generationStatusOrchestrating,
          );
        },
      );
      setActivePlatform('tp');

      const processedSegments = processAIResponseToSegments(aiResponse, activeZones, sport);
      const zwo = generateZwo(processedSegments, aiResponse.workoutName, currentPrompt, sport);
      const md = generateIntervalsMarkdown(processedSegments, sport, runIntensity);

      const newResult: CachedResult = {
        xml: zwo,
        markdown: md,
        segments: processedSegments,
        aiWorkout: aiResponse,
        workoutName: aiResponse.workoutName || "workout",
        sourcePrompt: currentPrompt,
        sport,
        runIntensity: sport === 'run' ? runIntensity : undefined,
      };

      setResultsCache((prev) => {
        const next = { ...prev, [currentCacheKey]: newResult };
        savePersistedWorkoutSession(currentCacheKey, next);
        return next;
      });

      if (session) {
        const { data: insertedRow, error: insertError } = await supabase
          .from("workouts")
          .insert(buildWorkoutInsertRow(user.id, newResult, currentPrompt))
          .select('id')
          .single();
        if (insertError) {
          console.error("[useWorkoutGenerator] Error saving workout to Supabase:", insertError);
          toast.error(t.workoutSaveDbError.replace("{message}", insertError.message));
        } else {
          newResult.workoutId = insertedRow?.id;
          setResultsCache((prev) => {
            const next = { ...prev, [currentCacheKey]: { ...newResult } };
            savePersistedWorkoutSession(currentCacheKey, next);
            return next;
          });
          toast.success(t.workoutSaved);
          onWorkoutSavedToHistory?.();
        }
        await fetchWorkouts();
      } else {
        if (posthog) {
          posthog.capture("workout_generated", {
            sport: sport,
            runIntensity: runIntensity,
            language: language,
            isPro: isPro,
            promptLength: currentPrompt.length,
            workoutName: newResult.workoutName,
            segmentsCount: newResult.segments.length,
            userType: session ? (isPro ? "pro" : "authenticated") : "guest",
          });
        }
        toast.success(t.workoutGenerated);
        setGuestGenerations((prev: number) => prev + 1);
      }

    } catch (err: unknown) {
      console.error("[useWorkoutGenerator] Error in handleGenerate():", err);
      let displayError = t.error;
      const rawMsg = err instanceof Error ? err.message : String(err ?? "");

      if (rawMsg) {
        try {
          const backendError = JSON.parse(rawMsg) as {
            error?: string;
            retryTime?: number;
            message?: string;
          };
          if (backendError.error === "GEMINI_QUOTA_EXCEEDED") {
            const retryTime = Math.ceil(Number(backendError.retryTime) || 0);
            displayError = t.errorGeminiQuotaExceeded.replace("{retryTime}", `${retryTime}s`);
          } else if (backendError.error === "GUEST_LIMIT_EXCEEDED") {
            displayError = t.guestLimitExceeded;
          } else if (backendError.error === "FREE_TIER_EXHAUSTED") {
            displayError = t.freeTierExhausted;
          } else if (backendError.error === "MISSING_API_KEY") {
            displayError = t.errorMissingApiKeyServer;
          } else if (backendError.error === "AI_OVERLOADED") {
            displayError = t.errorAiOverloaded;
          } else if (backendError.error === "JOB_TIMEOUT") {
            displayError = t.errorJobTimeout;
          } else if (
            backendError.error === "MISSING_SERVICE_KEY" ||
            backendError.error === "BACKGROUND_INVOKE_FAILED" ||
            backendError.error === "JOB_CREATE_FAILED"
          ) {
            displayError = t.errorJobEnqueue;
          } else if (backendError.error === "PROMPT_UNSAFE") {
            displayError = t.errorPromptUnsafe;
          } else if (backendError.error === "GUEST_ENQUEUE_RATE_LIMIT") {
            displayError = t.errorGuestEnqueueRateLimit;
          } else if (backendError.error === "TURNSTILE_REQUIRED" || backendError.error === "TURNSTILE_FAILED") {
            displayError =
              backendError.error === "TURNSTILE_REQUIRED" ? t.errorTurnstileRequired : t.errorTurnstileFailed;
          } else if (backendError.error === "UNAUTHORIZED") {
            displayError = t.errorUnauthorizedServer;
          } else if (backendError.error === "AI_INVALID_RESPONSE") {
            displayError = t.errorAiInvalidResponse;
          } else if (typeof backendError.error === "string" && backendError.error.length > 0) {
            displayError = backendError.error;
          } else if (backendError.message) {
            displayError = backendError.message;
          } else {
            displayError = rawMsg;
          }
        } catch {
          if (
            rawMsg.includes('Error 504') ||
            rawMsg.includes('Serwer pracuje nad Twoim zadaniem') ||
            rawMsg.includes('still working on your task')
          ) {
            displayError = t.errorGateway504Html;
          } else {
            displayError = rawMsg || t.error;
          }
        }
      }

      toast.error(displayError);
      setShowLandingPage(false);
    } finally {
      setGenerationPhaseLabel(null);
      setIsLoading(false);
    }
  }, [user, currentPrompt, activeZones, sport, runIntensity, activeIntensity, isZonesValid, language, currentCacheKey, fetchWorkouts, t, session, guestGenerations, setGuestGenerations, setShowGuestLimitModal, isPro, setShowPricingModal, setShowLandingPage, onWorkoutSavedToHistory]);

  return {
    isLoading,
    generationPhaseLabel,
    resultsCache,
    setResultsCache,
    activePlatform,
    setActivePlatform,
    segments,
    aiWorkout,
    generatedXml,
    generatedMarkdown,
    workoutName,
    currentWorkoutId,
    handleGenerate,
  };
}
